import React from "react";
import AppIcon from "components/ui/AppIcon.jsx";

const AppSearchInput = ({
  value,
  type = "text",
  onChange,
  onInput,
  placeholder = "Search",
  disabled = false,
  ...restProps
}) => {
  return (
    <div className="w-full">
      <div
        className={`flex items-center gap-3 rounded-xl border px-4 py-3
        ${
          disabled
            ? "bg-gray-50 border-gray-200"
            : "bg-white border-gray-300 focus-within:border-blue-500"
        }`}
      >
        <AppIcon name="search" />

        <input
          {...restProps}
          type={type}
          value={value}
          onChange={onChange}
          onInput={onInput}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 bg-transparent text-sm text-gray-700
                     placeholder-gray-400 outline-none"
        />
      </div>
    </div>
  );
};

export const DebouncedSearchInput = ({
  value: externalValue = "",
  onDebouncedChange = () => {},
  delay = 500,
  minLength = 0,
  placeholder,
  ...restProps
}) => {
  const [inputValue, setInputValue] = React.useState(externalValue);
  const isFirstRender = React.useRef(true);
  const lastEmittedRef = React.useRef(externalValue);

  // Keep local state in sync if parent resets the value externally
  // (e.g. clearing search when filter changes)
  React.useEffect(() => {
    if (externalValue !== lastEmittedRef.current) {
      setInputValue(externalValue);
      lastEmittedRef.current = externalValue;
    }
  }, [externalValue]);

  React.useEffect(() => {
    // Don't fire on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const trimmed = inputValue?.toString?.()?.trim?.() ?? "";

    // Don't re-emit the same value
    if (trimmed === lastEmittedRef.current) return;

    // Empty value: emit immediately (no debounce needed for clearing)
    if (trimmed === "") {
      lastEmittedRef.current = "";
      onDebouncedChange("");
      return;
    }

    // Below min length: don't emit
    if (trimmed.length < minLength) return;

    const handler = setTimeout(() => {
      lastEmittedRef.current = trimmed;
      onDebouncedChange(trimmed);
    }, delay);

    return () => clearTimeout(handler);
  }, [inputValue, delay, minLength, onDebouncedChange]);

  return (
    <AppSearchInput
      {...restProps}
      value={inputValue}
      onChange={(e) => setInputValue(e?.target?.value ?? "")}
      placeholder={placeholder}
    />
  );
};

export default AppSearchInput;